import type { Rectangle } from 'pixi.js';
import { TextMetrics, tallestStrings } from '../../TextAsset/metrics/TextMetrics';
import { DynamicLevel, TextAlignment, SeekMode } from '../../../../enums';
import type { TextStyleRendererSettings } from '../../TextAsset/renderer/TextStyleRenderer';
import { TextStyleRenderer } from '../../TextAsset/renderer/TextStyleRenderer';
import type { Transcript } from '../../../Transcript';
import { getTotalBounds } from '../../TextAsset/textMetricsHelpers';
import type { CanvasRendererSettings } from '../../TextAsset/renderer/BaseTextRenderer';
import { CanvasRenderer } from '../../TextAsset/renderer/BaseTextRenderer';
import type { TextDynamicGraphicRenderer } from '../../TextAsset/renderer/TextDynamicGraphicRenderer';
import { buildMetricsForFancyCaptions } from '../metrics/FancyCaptionMetrics';

export interface FancyCaptionRendererSettings extends CanvasRendererSettings {
    transcript: Transcript;
    alignment: TextAlignment;
    normalStyle: TextStyleRendererSettings;
    spokenWordStyle: TextStyleRendererSettings;
    isSpokenWordStyleEnabled: boolean;
}

enum StyledDataTypes {
    normalStyle = 'normalStyle',
    spokenWordStyle = 'spokenWordStyle',
}

class FancyCaptionRendererRuntimeStyledData {
    /** The build text metrics for each chunk of text. */
    textMetrics: TextMetrics[] = [];
    /** The build style metrics for each chunk of text. */
    styleMetrics: TextStyleRenderer[] = [];
    /** The bounds of the text block including the graphics for each chunk. */
    textWithGraphicsBounds: Rectangle[] = [];
    style: TextStyleRenderer = new TextStyleRenderer();
    styledDataType: StyledDataTypes;

    constructor(styledDataType: StyledDataTypes) {
        this.styledDataType = styledDataType;
    }

    clearBuiltData() {
        this.textMetrics = [];
        this.styleMetrics = [];
        this.textWithGraphicsBounds = [];
    }
}

/**
 * We keep the chunk visible until the last word endTime + WORD_THRESHOLD
 */
const WORD_THRESHOLD = 1000;

/**
 * Fancy captions render class. This class is responsible for animating and rendering the text and graphics from a transcript.
 */
export class FancyCaptionRenderer extends CanvasRenderer<FancyCaptionRendererSettings> {
    transcript: Transcript = null;

    #activeChunkIndex: number = 0;

    #normalStyledData = new FancyCaptionRendererRuntimeStyledData(StyledDataTypes.normalStyle);
    #spokenWordStyledData = new FancyCaptionRendererRuntimeStyledData(
        StyledDataTypes.spokenWordStyle,
    );

    get spokenWordStyledData() {
        return this.#spokenWordStyledData;
    }

    set spokenWordStyledData(newData: FancyCaptionRendererRuntimeStyledData) {
        this.#spokenWordStyledData = newData;
    }

    #totalBounds: Rectangle = null;
    #animationDuration: number = null;
    #maxFirstLineAscent: number = null;

    alignment = TextAlignment.LEFT;
    isSpokenWordStyleEnabled = false;

    get normalStyle() {
        return this.#normalStyledData.style;
    }

    get spokenWordStyle() {
        return this.#spokenWordStyledData.style;
    }

    /** The total duration of all animation configurations */
    get animationDuration() {
        if (!this.#animationDuration) {
            this.#animationDuration = this.getAnimationDuration();
        }
        return this.#animationDuration;
    }

    getAnimationDuration() {
        let maxDuration = 0;
        const callback = (config) => {
            if (maxDuration < config.duration) {
                maxDuration = config.duration;
            }
        };
        this.#normalStyledData.style.animationConfigurations.forEach(callback);
        this.#normalStyledData.style.graphics.forEach(callback);
        this.#spokenWordStyledData.style.animationConfigurations.forEach(callback);
        this.#spokenWordStyledData.style.graphics.forEach(callback);
        return maxDuration;
    }

    get #activeStyles() {
        if (
            this.#normalStyledData.styleMetrics.length === 0 ||
            this.#spokenWordStyledData.styleMetrics.length === 0
        ) {
            throw new Error('styles is empty');
        }
        return {
            normalStyle: this.#normalStyledData.styleMetrics[this.activeIndex],
            spokenWordStyle: this.#spokenWordStyledData.styleMetrics[this.activeIndex],
        };
    }

    get activeIndex(): number {
        return this.#activeChunkIndex;
    }

    get activeTexts() {
        if (
            this.#normalStyledData.textMetrics.length === 0 ||
            this.#spokenWordStyledData.textMetrics.length === 0
        ) {
            throw new Error('textMetrices is empty');
        }
        return {
            normalStyle: this.#normalStyledData.textMetrics[this.activeIndex],
            spokenWordStyle: this.#spokenWordStyledData.textMetrics[this.activeIndex],
        };
    }

    getChunkTexts() {
        return TextMetrics.getChunkTexts(this.#normalStyledData.textMetrics);
    }

    getChunkTimeRanges() {
        return TextMetrics.getChunkTimeRanges(this.#normalStyledData.textMetrics, this.transcript);
    }

    /** Get the tallest text from all the chunks of text. */
    getTallestText() {
        if (!this.transcript) {
            throw new Error('no transcript');
        }
        return tallestStrings(
            [...this.#normalStyledData.textMetrics, ...this.#spokenWordStyledData.textMetrics].map(
                (m) => m.getTallestWordElements(),
            ),
        ).join('');
    }

    /** Get the transcript word start and end time of the chunk at the specified index. */
    getTranscriptChunkTimeRange(chunkIndex: number) {
        if (this.#normalStyledData.textMetrics.length === 0) {
            return { startTime: 0, endTime: 0 };
        }
        const [wordStartIndex, wordEndIndex] =
            this.#normalStyledData.textMetrics[chunkIndex].sourceWordReferenceIndexRange;
        const startTime = this.transcript.words[wordStartIndex].startTime;
        const endTime = this.transcript.words[wordEndIndex].endTime;
        return { startTime, endTime };
    }

    /** Sets the active or visible text metrics based on the current time. **/
    override _onSetCurrentTime() {
        if (!this.transcript) {
            this.#activeChunkIndex = -1;
            return;
        }
        const futureTime = this.currentTime + this.chunkTimeOffset;
        const activeWordIndex = this.transcript.getActiveWordIndexAtTimeWithinThreshold(
            futureTime,
            WORD_THRESHOLD,
        );
        if (activeWordIndex === -1) {
            this.#activeChunkIndex = -1;
            return;
        }
        this.#activeChunkIndex = this.#normalStyledData.textMetrics.findIndex((m) => {
            const [startIndex, endIndex] = m.sourceWordReferenceIndexRange;
            return startIndex <= activeWordIndex && endIndex >= activeWordIndex;
        });
    }

    /**
     * Depending on the seek mode, we need to resolve the time offset differently. If we're in single frame mode, then
     * we want to show the end frame of the chunk without any animation. If it's in can play mode, then we want to add
     * the animation duration to the end of the chunk so we can gradually animate to the next chunk.
     */
    get chunkTimeOffset(): number {
        return this.seekMode === SeekMode.ResolveWhenSingleFrame ? 0 : this.animationDuration;
    }

    /** The bounds of the text block including the graphics. */
    get textWithGraphicsBounds() {
        return {
            normalStyle: this.#normalStyledData.textWithGraphicsBounds[this.activeIndex],
            spokenWordStyle: this.#spokenWordStyledData.textWithGraphicsBounds[this.activeIndex],
        };
    }

    // @inheritdoc
    override get hasText(): boolean {
        return this.transcript.words.length > 0;
    }

    // @inheritdoc
    override get canRender(): boolean {
        // we can only render when we've built the text metrics and we seeked to an active index
        return (
            this.#normalStyledData?.textMetrics &&
            this.#normalStyledData.textMetrics.length > 0 &&
            this.#spokenWordStyledData?.textMetrics &&
            this.#spokenWordStyledData.textMetrics.length > 0 &&
            this.activeIndex !== -1
        );
    }

    get chunkCount(): number {
        return this.#normalStyledData.textMetrics.length;
    }

    get hasChunks(): boolean {
        return this.#normalStyledData.textMetrics.length > 0;
    }

    get chunks() {
        return {
            normalStyle: this.#normalStyledData.textMetrics,
            spokenWordStyle: this.#spokenWordStyledData.textMetrics,
        };
    }

    get totalBounds(): Rectangle {
        return this.#totalBounds;
    }

    get activeTextBounds(): Rectangle | null {
        return this.#normalStyledData.textMetrics[this.activeIndex]?.bounds ?? null;
    }

    /**
     * The value which stabilizes the ascent of the first line of text. This ensures the baseline doesn't jump between
     * different chunks. We do this by removing the ascent of the first line of text from the max ascent of the first
     * line from all chunks.
     **/
    override get activeAscentStabilizer(): number {
        if (this.activeTexts.normalStyle.lines.length === 0) {
            return 0;
        }
        return this.maxFirstLineAscent - this.activeTexts.normalStyle.lines[0].ascent;
    }

    override apply(settings: FancyCaptionRendererSettings) {
        super.apply(settings);
        this.transcript = settings.transcript;
        this.alignment = settings.alignment;
        this.isSpokenWordStyleEnabled = settings.isSpokenWordStyleEnabled;
        this.#normalStyledData.style.apply(settings.normalStyle);
        this.#spokenWordStyledData.style.apply(settings.spokenWordStyle);
    }

    /** Build text, style and graphics metrics for each chunk of the transcript text */
    build() {
        if (!this.transcript) {
            return;
        }
        this.resetBuild();

        this.#buildForStyle(this.#normalStyledData);
        this.#buildForStyle(this.#spokenWordStyledData);
        this.#totalBounds = getTotalBounds([
            ...this.#normalStyledData.textWithGraphicsBounds,
            ...this.#spokenWordStyledData.textWithGraphicsBounds,
        ]);
    }

    #buildForStyle(styledData: FancyCaptionRendererRuntimeStyledData) {
        styledData.textMetrics = buildMetricsForFancyCaptions({
            transcript: this.transcript,
            alignment: this.alignment,
            style: styledData.style,
            maxWidth: this.maxTextWidth,
            maxHeight: this.maxTextHeight,
        });

        if (styledData.textMetrics.length === 0) {
            return;
        }

        styledData.textMetrics.map((textMetrics) => {
            const bounds: Rectangle[] = [textMetrics.bounds];

            // create a clone of the style and build the graphics for the current text metrics to retrieve during draw.
            // remember that for our fancy captions we have a list of text metrics and thus a list of style metrics.
            // each one has unique metrics.
            const clone = styledData.style.clone();
            textMetrics.updateStyles(clone, clone);
            clone.graphics.forEach((graphic) => {
                graphic.build(textMetrics);
                if (!graphic.asMask) bounds.push(graphic.bounds);
            });
            styledData.styleMetrics.push(clone);
            styledData.textWithGraphicsBounds.push(getTotalBounds(bounds));
        });
    }

    /**
     * Compute the animation values for each graphic and text element. This method requires currentTime, transcript and
     * activeText to be set to calculate the correct animation values.
     * */
    _computeAnimationValues() {
        this.#computeAnimationValuesByStyle(
            this.#activeStyles.normalStyle,
            this.activeTexts.normalStyle,
        );
        this.#computeAnimationValuesByStyle(
            this.#activeStyles.spokenWordStyle,
            this.activeTexts.spokenWordStyle,
        );
    }

    #computeAnimationValuesByStyle(activeStyle: TextStyleRenderer, activeText: TextMetrics) {
        // compute the animation values for each graphic
        activeStyle.graphics.forEach((graphic) => {
            graphic.animationConfigurations.forEach((animation) => {
                animation.computeTranscriptAnimationValues(
                    this.currentTime,
                    this.transcript,
                    activeText,
                );
            });
        });
        // compute the animation values our text
        activeStyle.animationConfigurations.forEach((animation) => {
            animation.computeTranscriptAnimationValues(
                this.currentTime,
                this.transcript,
                activeText,
            );
        });
    }

    get maxFirstLineAscent() {
        if (this.#maxFirstLineAscent === null) {
            this.#maxFirstLineAscent = this._maxFirstLineAscent();
        }
        return this.#maxFirstLineAscent;
    }

    _maxFirstLineAscent() {
        let maxBaseline = 0;
        [...this.#normalStyledData.textMetrics, ...this.#spokenWordStyledData.textMetrics].forEach(
            (metrics) => {
                if (maxBaseline < metrics.lines[0].ascent) {
                    maxBaseline = metrics.lines[0].ascent;
                }
            },
        );
        return maxBaseline;
    }

    /** Draw the text and style of the active chunk */
    override draw() {
        if (!this.canvas) {
            return;
        }
        if (!this.canRender || this.transcript.empty) {
            this._resetCanvas();
            return;
        }
        this._computeAnimationValues();

        super.draw();

        this.context.save();
        this.#applyObjectScaleAnimation(this.#activeStyles.normalStyle);
        this.#clipObject(this.#activeStyles.normalStyle);
        this.#drawGraphics();
        this.#maskAndDrawText();
        this.context.restore();
    }

    /**
     * Returns the index of the spoken word in the active chunk.
     */
    get #spokenWordIndexInActiveChunk() {
        let wordsSeenSoFar = 0;
        for (const line of this.activeTexts.normalStyle.lines) {
            const [lineStartWordIndex] = line.sourceWordReferenceIndexRange;
            for (let wordIndInLine = 0; wordIndInLine < line.visibleWords.length; wordIndInLine++) {
                const absoluteWordIndex = lineStartWordIndex + wordIndInLine;
                if (absoluteWordIndex >= this.transcript.words.length) {
                    break;
                }
                const endTime =
                    absoluteWordIndex + 1 >= line.visibleWords.length
                        ? this.transcript.words[absoluteWordIndex].endTime
                        : this.transcript.words[absoluteWordIndex + 1].startTime;
                if (
                    this.currentTime >= this.transcript.words[absoluteWordIndex].startTime &&
                    this.currentTime < endTime
                ) {
                    return wordsSeenSoFar + wordIndInLine;
                }
            }
            wordsSeenSoFar += line.visibleWords.length;
        }
        return -1;
    }

    /**
     * Apply any object scale animation to the canvas context. We do this here once globally instead of for each word
     * element for simplicity and performance.
     */
    #applyObjectScaleAnimation(activeStyle: TextStyleRenderer) {
        let scaleX = 1;
        let scaleY = 1;
        // scale from the center of the total bounds
        const scaleAnchorX = 0.5 * this.totalBounds.width;
        const scaleAnchorY = 0.5 * this.totalBounds.height;
        activeStyle.animationConfigurations
            .filter(
                (config) =>
                    config.level === DynamicLevel.object &&
                    (config.property === 'scaleX' || config.property === 'scaleY'),
            )
            .forEach((config) => {
                switch (config.property) {
                    case 'scaleX':
                        scaleX = config.computedValues[0];
                        break;
                    case 'scaleY':
                        scaleY = config.computedValues[0];
                        break;
                }
            });
        if (scaleX === 1 && scaleY === 1) {
            return;
        }
        this.context.translate(scaleAnchorX, scaleAnchorY);
        this.context.scale(scaleX, scaleY);
        this.context.translate(-scaleAnchorX, -scaleAnchorY);
    }

    /**
     * Transform the canvas to the active bounds. For FancyCaptions we need to use our total bounds transformation so
     * that we can align the active bounds within the total bounds thereby keeping their position in Y consistent
     * between chunks.
     */
    override _applyActiveBoundsTransformation() {
        // apply total bounds transformation
        const canvasAnchorX = -(this.anchorX * this.totalBounds.width);
        const canvasAnchorY = -(this.anchorY * this.totalBounds.height);
        this.context.translate(canvasAnchorX, canvasAnchorY);
        this.context.translate(this.translateX, this.translateY);
        this.context.translate(-this.totalBounds.x, -this.totalBounds.y);

        // align the active bounds within the total bounds
        const activeBoundsXOffset =
            (this.totalBounds.width - this.activeTextBounds.width) * this.anchorX;
        this.context.translate(activeBoundsXOffset, 0);

        // move the active bounds to the top border of the total bounds
        this.context.translate(0, this.totalBounds.y - this.activeTextBounds.y);

        // stabilize the baseline between the different chunks
        this.context.translate(0, this.activeAscentStabilizer);
    }

    /**
     * Apply the object masks, since they mask everything, including object level graphics.
     */
    #clipObject(activeStyle: TextStyleRenderer) {
        const objectMasks = this.#filterAndOrderGraphics({
            level: DynamicLevel.object,
            mask: true,
            activeStyle,
        });
        objectMasks.forEach((mask) => mask.clipElement(this.context, 0));
    }

    /**
     * Draw all the graphics in level order of object, line, word.
     */
    #drawGraphics() {
        // Draw the graphics in level order: in the editor (and therefore scene design) the graphics can be created in
        // any level order. We need to extract them and draw them in the correct order.
        const objectGraphics = this.#filterAndOrderGraphics({
            level: DynamicLevel.object,
            mask: false,
            activeStyle: this.#activeStyles.normalStyle,
        });
        const lineGraphics = this.#filterAndOrderGraphics({
            level: DynamicLevel.line,
            mask: false,
            activeStyle: this.#activeStyles.normalStyle,
        });
        const normalWordGraphics = this.#filterAndOrderGraphics({
            level: DynamicLevel.word,
            mask: false,
            activeStyle: this.#activeStyles.normalStyle,
        });
        const spokenWordWordGraphics = this.#filterAndOrderGraphics({
            level: DynamicLevel.word,
            mask: false,
            activeStyle: this.#activeStyles.spokenWordStyle,
        });

        objectGraphics.forEach((graphic) => graphic.draw(this.context, undefined));
        lineGraphics.forEach((graphic) => graphic.draw(this.context, undefined));

        if (this.isSpokenWordStyleEnabled) {
            const spokenWordInd = this.#spokenWordIndexInActiveChunk;
            normalWordGraphics.forEach((graphic) => {
                graphic.draw(this.context, (i) => {
                    return i !== spokenWordInd && i !== spokenWordInd + 1;
                });
            });
            spokenWordWordGraphics.forEach((graphic) => {
                graphic.draw(this.context, (i) => {
                    return i === spokenWordInd || i === spokenWordInd + 1;
                });
            });
        } else {
            normalWordGraphics.forEach((graphic) => {
                graphic.draw(this.context, undefined);
            });
        }
    }

    /**
     * Draw text, masking it appropriately at each of line and word levels.
     */
    #maskAndDrawText() {
        const normalLineMasks = this.#filterAndOrderGraphics({
            level: DynamicLevel.line,
            mask: true,
            activeStyle: this.#activeStyles.normalStyle,
        });
        const normalWordMasks = this.#filterAndOrderGraphics({
            level: DynamicLevel.word,
            mask: true,
            activeStyle: this.#activeStyles.normalStyle,
        });

        const spokenWordMasks = this.#filterAndOrderGraphics({
            level: DynamicLevel.word,
            mask: true,
            activeStyle: this.#activeStyles.spokenWordStyle,
        });
        // Track the current word index, there is no 1:1 mapping from word masks to word metrics due to breaking spaces.
        let ixWord = 0;
        this.activeTexts.normalStyle.lines.forEach((line, ixLine) => {
            this.context.save();
            normalLineMasks.forEach((mask) => mask.clipElement(this.context, ixLine));
            const [lineStartWordIndex] = line.sourceWordReferenceIndexRange;
            line.visibleWords.forEach((word, wordIndInLine) => {
                this.context.save();
                const absoluteWordIndex = lineStartWordIndex + wordIndInLine;
                if (
                    this.isSpokenWordStyleEnabled &&
                    this.currentTime >= this.transcript.words[absoluteWordIndex].startTime &&
                    this.currentTime < this.transcript.words[absoluteWordIndex].endTime
                ) {
                    spokenWordMasks.forEach((mask) => mask.clipElement(this.context, ixWord));
                    this.activeTexts.spokenWordStyle.lines[ixLine].visibleWords[
                        wordIndInLine
                    ].elements.forEach((element) => {
                        element.draw(this.context);
                    });
                } else {
                    normalWordMasks.forEach((mask) => mask.clipElement(this.context, ixWord));
                    word.elements.forEach((element) => {
                        element.draw(this.context);
                    });
                }
                this.context.restore();
                ixWord += 1;
            });
            this.context.restore();
        });
    }

    /**
     * Filter the graphics by level and mask status. Finally, will reverse the list so that we draw the graphics
     * from top to bottom. So the first graphic in the list will be the top-most graphic. This is how we present
     * the order in the editor.
     */
    #filterAndOrderGraphics({
        level,
        mask,
        activeStyle,
    }: {
        level: DynamicLevel;
        mask: boolean;
        activeStyle: TextStyleRenderer;
    }): TextDynamicGraphicRenderer[] {
        return activeStyle.graphics
            .filter((graphic) => graphic.level === level && graphic.asMask === mask)
            .reverse();
    }

    override resetBuild() {
        this.#normalStyledData.clearBuiltData();
        this.#spokenWordStyledData.clearBuiltData();
        this.#totalBounds = null;
        this.#activeChunkIndex = 0;
        this.#animationDuration = null;
        this.#maxFirstLineAscent = null;
    }
}
